import { NavLink, Outlet } from 'react-router';
import {
  Building2,
  ClipboardList,
  CreditCard,
  Files,
  Home,
  Receipt,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { AccountMenu } from '../../components/AccountMenu.js';
import { BrandMark } from '../../components/BrandMark.js';
import { SessionBar } from '../../components/SessionBar.js';
import { TenantSmsConsentPrompt } from '../../components/TenantSmsConsentPrompt.js';
import { useActor } from '../../lib/capabilities.js';

/**
 * Shell for all three portal audiences.
 *
 * One shell, three navs. All three hit the same endpoints — RLS decides what
 * comes back, so there is no per-audience query to keep in sync — but they are
 * answering different questions, and a resident looking for "what do I owe" should
 * not be reading a nav item called Residents.
 *
 * The nav is chosen from the actor type rather than from capabilities, because the
 * three portals are different products rather than the same product with more or
 * fewer buttons. Capability checks stay inside the screens.
 */
const OWNER_LINKS: { to: string; label: string; icon: LucideIcon; end?: boolean }[] = [
  // Renamed from "Portfolio" — the landing page no longer shows one (see
  // OwnerHome.tsx). "Properties" is new: it's what used to live inline on
  // that page, and without a link here /portal/owner/properties/:id would
  // have had no way to be reached at all.
  { to: '/portal/owner', label: 'Home', icon: Home, end: true },
  { to: '/portal/owner/properties', label: 'Properties', icon: Building2 },
  { to: '/portal/owner/residents', label: 'Residents', icon: Users },
  { to: '/portal/owner/statements', label: 'Statements', icon: Receipt },
  { to: '/portal/owner/payouts', label: 'Payouts', icon: CreditCard },
  { to: '/portal/owner/documents', label: 'Documents', icon: Files },
];

const TENANT_LINKS: { to: string; label: string; icon: LucideIcon; end?: boolean }[] = [
  { to: '/portal/tenant', label: 'My home', icon: Home, end: true },
  { to: '/portal/tenant/pay', label: 'Pay rent', icon: CreditCard },
  { to: '/portal/tenant/maintenance', label: 'Maintenance', icon: Wrench },
  { to: '/portal/tenant/documents', label: 'Documents', icon: Files },
];

const VENDOR_LINKS: { to: string; label: string; icon: LucideIcon; end?: boolean }[] = [
  { to: '/portal/vendor', label: 'Home', icon: Home, end: true },
  { to: '/portal/vendor/work-orders', label: 'Work Orders', icon: ClipboardList },
  { to: '/portal/vendor/payouts', label: 'Payouts', icon: CreditCard },
  { to: '/portal/vendor/disbursements', label: 'History', icon: Receipt },
];

function badgeLabel(actorType: string | undefined): string {
  if (actorType === 'OWNER') return 'Owner';
  if (actorType === 'VENDOR') return 'Vendor';
  return 'Resident';
}

export function PortalLayout() {
  const actor = useActor();
  const links =
    actor?.actorType === 'OWNER'
      ? OWNER_LINKS
      : actor?.actorType === 'VENDOR'
        ? VENDOR_LINKS
        : TENANT_LINKS;

  return (
    <div className="shell">
      <header className="shell__header">
        {/* `.shell__header-row` is a row-direction flex container — needed here
            because `.shell__header` itself is column-direction with no
            `align-items` set, so its default `stretch` would otherwise stretch
            BrandMark's `width: auto` logo to the container's full width. Same
            fix StaffLayout already applies. */}
        <div className="shell__header-row">
          <BrandMark />
          <span className="shell__badge shell__badge--portal">{badgeLabel(actor?.actorType)}</span>
          <SessionBar />
          <AccountMenu />
        </div>
        <nav className="row">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end ?? false}
              className="button button--quiet portal-nav-link"
            >
              <link.icon size={16} strokeWidth={1.8} aria-hidden="true" />
              {link.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="shell__main">
        <Outlet />
      </main>
      {actor?.actorType === 'TENANT' ? <TenantSmsConsentPrompt /> : null}
    </div>
  );
}
